import type {
  ReadingOperationStrategicTaskRevision,
  ReportedProcedure,
} from "../../../../../../../models/operation-strategic-task-model";

export type AikpiProcedureDiffRow = {
  procedure: ReportedProcedure;
  hasDiff: boolean;
};

export type AikpiItemDiffRow = {
  link: ReadingOperationStrategicTaskRevision;
  hasDiff: boolean;
  procedureCount: number;
  procedures: AikpiProcedureDiffRow[];
};

export function recordedProcedureIds(link: ReadingOperationStrategicTaskRevision): number[] {
  if (link.reportedProcedures) return link.reportedProcedures.map(({ id }) => id);

  return (link.procedures ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter((id) => /^\d+$/.test(id))
    .map(Number);
}

export function diffAikpiItems(
  links: ReadingOperationStrategicTaskRevision[] | undefined,
  counterparts: ReadingOperationStrategicTaskRevision[] | undefined
): AikpiItemDiffRow[] {
  const counterpartsById = new Map((counterparts ?? []).map((link) => [link.operationStrategicTaskId, link]));

  return (links ?? []).map((link) => {
    const counterpart = counterpartsById.get(link.operationStrategicTaskId);

    return {
      link,
      hasDiff: !counterpart || linkSignature(link) !== linkSignature(counterpart),
      procedureCount: recordedProcedureIds(link).length,
      procedures: (link.reportedProcedures ?? []).map((procedure) => ({
        procedure,
        hasDiff: hasProcedureChanged(procedure, counterpart),
      })),
    };
  });
}

export function haveAikpiItemsChanged(
  newLinks: ReadingOperationStrategicTaskRevision[] | undefined,
  oldLinks: ReadingOperationStrategicTaskRevision[] | undefined
): boolean {
  if ((newLinks ?? []).length !== (oldLinks ?? []).length) return true;

  return diffAikpiItems(newLinks, oldLinks).some(({ hasDiff }) => hasDiff);
}

function hasProcedureChanged(
  procedure: ReportedProcedure,
  counterpart: ReadingOperationStrategicTaskRevision | undefined
): boolean {
  if (!counterpart) return false;

  const counterpartProcedure = counterpart.reportedProcedures?.find(({ id }) => id === procedure.id);
  if (!counterpartProcedure) return true;

  return procedureSignature(procedure) !== procedureSignature(counterpartProcedure);
}

function linkSignature(link: ReadingOperationStrategicTaskRevision): string {
  if (link.reportedProcedures) return link.reportedProcedures.map(procedureSignature).sort().join("|");

  return recordedProcedureIds(link)
    .sort((a, b) => a - b)
    .join("|");
}

function procedureSignature(procedure: ReportedProcedure): string {
  const agentIds = (procedure.aiAgents ?? []).map(({ id }) => id).sort((a, b) => a - b);

  return `${procedure.id}:${procedure.responsibilityType ?? ""}:${agentIds.join(",")}`;
}
